import React, { Component, ReactElement } from "react";
import { store } from "@store";
import { stringNormalize } from "@bonliva-crm/common";
import MarkHighlighter from "@common/MarkHighlighter";
import { EndOfList } from "./EndOfList";
import { Placeholder } from "./Placeholder";
import { EmptyList } from "./EmptyList";
import debug from "debug";

const log = debug("app:common:SearchResult");

interface IProps<T> {
  noResultsMessage?: string;
  scrollContainerId?: string;
  renderDetail: (item: T, key: number) => React.ReactNode;
  renderGroup?: any;
  fetchData: (skip: number) => Promise<T[]>;
  compare?: any;
  groupBy?: any;
  offsetTop?: number;
  extraBottomSpaceHeight?: number;
  ref?: any;
}

class SearchResult extends Component<IProps<any>, {}> {
  static defaultProps = {
    offsetTop: 500,
    extraBottomSpaceHeight: 5000
  };
  lastGroup = {};
  search = { version: 0, search: { text: "" } };
  rendered = [];
  skip = 0;
  loading = true;
  hasMore = true;
  animationRunning = false;
  scrollContainer: Element | Window;
  loader: HTMLElement;
  prev: any;
  key = 0;
  unset: () => void;

  constructor(props: IProps<any>) {
    super(props);
    log("constructor", props);
    this.loadMore = this.loadMore.bind(this);
    this.searchCallback = this.searchCallback.bind(this);
  }

  checkContainerScroll = (container) => {
    log("checkScrollContainer", container);

    if (this.loading || !this.hasMore || this.animationRunning) {
      return;
    }

    window.requestAnimationFrame((time) => {
      log("checkScrollContainer inside new frame", time);

      const clientHeight =
        container === document.body
          ? document.documentElement.clientHeight
          : container.clientHeight;

      const scrollTop =
        container === document.body ? window.scrollY : container.scrollTop;

      const containerBottomEdgePosition = scrollTop + clientHeight + this.props.offsetTop;

      if (containerBottomEdgePosition >= container.scrollHeight) {
        this.loadMore();
      }

      this.animationRunning = false;
    });
  };

  setEventListeners() {
    log("setEventListeners");

    if (!this.scrollContainer) {
      // todo: handle error
      console.error("Scroll container not found");
    }

    const checker = () => this.checkContainerScroll(this.scrollContainer);

    if (this.scrollContainer === window.document.body) {
      window.onscroll = checker;
      window.onresize = checker;

      this.unset = () => {
        window.onscroll = null;
        window.onresize = null;
      };
    } else {
      this.scrollContainer.addEventListener("scroll", checker, { passive: true });
      this.scrollContainer.addEventListener("resize", checker, { passive: true });

      this.unset = () => {
        this.scrollContainer.removeEventListener("scroll", checker);
        this.scrollContainer.removeEventListener("resize", checker);
      };
    }
  }

  setLoading(v) {
    log("setLoading", v);

    if (v) {
      this.loading = true;
      this.loader.style.height = `${this.props.extraBottomSpaceHeight}px`;
      this.loader.style.display = "block";
    } else {
      this.loading = false;
      this.loader.style.display = "none";
    }
  }

  async componentDidMount() {
    log("componentDidMount");

    this.scrollContainer = this.props.scrollContainerId
      ? window.document.querySelector(`#${this.props.scrollContainerId}`)
      : window.document.body;

    this.setEventListeners();
    this.loader = document.querySelector("#searchresult__placeholder");
    this.loader.style.display = "block";
  }

  componentWillUnmount() {
    this.unset && this.unset();
  }

  async loadMore() {
    log("loadMore()", { loading: this.loading, hasMore: this.hasMore });

    if (this.loading || !this.hasMore) {
      return;
    }

    log("loadMore() try loading");

    try {
      const sv = this.search.version;
      log("search version", sv);

      this.setLoading(true);
      const items = await this.props.fetchData(this.skip);
      this.skip += items.length;
      this.hasMore = items.length > 0;

      // request was cancelled
      if (sv !== this.search.version) {
        return;
      }

      const rendered = this.prerender(items);
      this.rendered.push(...rendered);
      if (items.length > 0) {
        this.prev = items[items.length - 1];
      }
    } catch (x) {
      console.warn(x);
      log(x);
    } finally {
      log("loadMore() forceUpdate");
      this.setLoading(false);
      this.forceUpdate();
    }
  }

  searchCallback() {
    log("searchCallback()");

    this.search.version++;
    this.loadFirst();
  }
  loadFirst() {
    log("loadFirst()");

    this.lastGroup = {};
    this.rendered = [];
    this.key = 0;
    this.loading = false;
    this.skip = 0;
    this.hasMore = true;
    this.loadMore();
  }

  prerender(items) {
    log("prerender", items);

    const { groupBy, renderDetail, renderGroup } = this.props;
    const res = [];
    items.forEach((item) => {
      if (groupBy) {
        const value = groupBy(item);
        if (value !== this.lastGroup) {
          const innerRow = React.cloneElement(renderGroup(item, this.key), {
            key: `Item${this.key}`
          });
          this.key++;
          res.push(innerRow);
          this.lastGroup = value;
        }
      }

      const row = React.cloneElement(renderDetail(item, this.key) as ReactElement<any>, {
        key: `Item${this.key}`
      });
      this.key++;
      res.push(row);
    });
    return res;
  }

  render() {
    log("render", { loading: this.loading, hasMore: this.hasMore });
    const res = [...this.rendered];
    const hl = store().generalSearchStore.searchParams.text;

    return (
      <MarkHighlighter highlightSearch={[hl, stringNormalize(hl)]}>
        <React.Fragment>
          {(!res || res.length === 0) && !this.loading && (
            <EmptyList>{this.props.noResultsMessage}</EmptyList>
          )}

          {res}

          <Placeholder
            height={this.props.extraBottomSpaceHeight}
            id="searchresult__placeholder"
          />

          {res && res.length > 0 && !this.loading && !this.hasMore && <EndOfList />}
        </React.Fragment>
      </MarkHighlighter>
    );
  }
}

export default SearchResult;
